"use client"

/**
 * The platform layouts' app check (spec multi-product 003 §9, 007 §4; board
 * unit WP3.4.2). Mounted once in the root layout above the billing gate, so
 * every platform route group is covered and a person whose current firm does
 * not resolve `app_platform` is sent to `homeFor` (a Bots-only firm to its
 * Bots home) without ever seeing a broker shell or the broker billing wall.
 *
 * A staff-scope firm opens only the staff shell (R0-11): on any other platform
 * page it is sent to `/cs` (`staffFirmHomeAt`) before the page renders.
 */
import { useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"

import { AppLoading } from "@/components/ui/app-loading"
import { useIdentity } from "@/lib/providers/identity"
import { useEntitlements } from "@/lib/providers/entitlements"

import { PLATFORM_ACCESS_KEY, platformRefusedAt, staffFirmHomeAt } from "../lib/platform-app-gate"
import { HomeRedirectGate } from "./home-redirect-gate"

export function PlatformAppGate({ children }: { readonly children: React.ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const { identity } = useIdentity()
  const { isResolved, hasFeature } = useEntitlements()
  const staffHome =
    pathname !== null && identity !== null ? staffFirmHomeAt({ pathname, firmScope: identity.firmScope }) : null
  const refused =
    staffHome === null &&
    pathname !== null &&
    identity !== null &&
    platformRefusedAt({ pathname, entitlementsResolved: isResolved, resolvesPlatform: hasFeature(PLATFORM_ACCESS_KEY) })

  useEffect(() => {
    if (staffHome !== null) router.replace(staffHome)
  }, [staffHome, router])

  if (staffHome !== null) return <AppLoading />
  return (
    <HomeRedirectGate refused={refused} currentOrgId={identity?.orgId ?? null} canSwitchFirm>
      {children}
    </HomeRedirectGate>
  )
}
